import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useMyBalances } from '../../hooks/useApiQuery';
import { Deposit } from '../../pages/Deposit';
import { Transfer } from '../../pages/Transfer';
import { Withdraw } from '../../pages/Withdraw';
import { formatUsd } from '../../lib/format';
import { BalanceTiles, Panel } from '../parts';
import { CabTabs } from '../tabs';

type Tab = 'deposit' | 'transfer' | 'withdraw';
const TABS: readonly Tab[] = ['deposit', 'transfer', 'withdraw'];

/**
 * Web wallet — two columns: the deposit / transfer / withdraw forms on
 * the left (reusing the embedded form components, so the withdraw
 * step-up — browser OTP here, initData in the TG app — works unchanged),
 * a live balance + in-transit summary on the right.
 */
export function CabWallet() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? '')
    ? (raw as Tab)
    : 'deposit';

  const balances = useMyBalances();
  const inTransit = balances.data?.in_transit ?? [];

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_minmax(300px,360px)]">
      <Panel className="min-w-0">
        <CabTabs
          fullWidth
          value={tab}
          onChange={(v) =>
            setParams((prev) => {
              const next = new URLSearchParams(prev);
              next.set('tab', v);
              return next;
            })
          }
          options={[
            { value: 'deposit', label: t('wallet.tab_deposit') },
            { value: 'transfer', label: t('wallet.tab_transfer') },
            { value: 'withdraw', label: t('wallet.tab_withdraw') },
          ]}
        />
        <div className="mt-3">
          {tab === 'deposit' ? (
            <Deposit embedded />
          ) : tab === 'transfer' ? (
            <Transfer embedded />
          ) : (
            <Withdraw embedded />
          )}
        </div>
      </Panel>

      <div className="space-y-5">
        <BalanceTiles b={balances.data} />
        {inTransit.length ? (
          <Panel title={t('cabinet.in_transit')}>
            <div className="divide-y divide-pb-border-soft">
              {inTransit.map((it) => (
                <div key={it.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] text-tg-text">
                      {it.direction}
                    </div>
                    <div className="pb-caption mt-0.5">
                      {it.current_step ?? it.status}
                    </div>
                  </div>
                  <span className="pb-numeral text-[13px] text-tg-text">
                    {formatUsd(it.amount_usdc)}
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        ) : null}
      </div>
    </div>
  );
}
